import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { STATEMENT_BUILDER_OPERATORS } from '@components/main/borrower/statement-builder/model';
import { MultiSelectItem } from '@components/shared/popover-menu-multi-select/popover-menu-multi-select.component';
import { Select2OptionData } from '@components/shared/select2/select2.interface';
import { Company } from '@models/company';
import { DataViewColumn, DataViewRow } from '@models/dataview';
import { AlertService } from '@services/alert.service';
import { DataFrameService } from '@services/dataframes.service';
import { DATE_FORMAT, SCENARIO_TYPE_HISTORICAL, SERVER_DATE_FORMAT, SUPPORTED_CURRENCIES, TAXONOMY_ITEM_IDS } from '@utils/constants';
import { availableScenarioTypes, resolveScenarioType } from '@utils/scenario-type';
import moment from 'moment';
import { finalize } from 'rxjs/operators';

declare var bootstrap: any;

/** Validator: at least one period index must be selected */
function atLeastOnePeriodSelected(control: AbstractControl): { [key: string]: boolean } | null {
  const arr = control.value;
  if (!Array.isArray(arr) || arr.length < 1) {
    return { required: true };
  }
  return null;
}

/** Validator: value must not be -1 (required selection from dropdown). */
function requiredSelection(control: AbstractControl): { [key: string]: boolean } | null {
  const val = control.value;
  if (val === -1 || val === undefined || val === null || val === '') {
    return { required: true };
  }
  return null;
}

/** Validator: a non-empty value must contain at least one non-whitespace character. */
function noWhitespaceOnly(control: AbstractControl): { [key: string]: boolean } | null {
  if (control.value && !control.value.trim()) {
    return { nospace: true };
  }
  return null;
}

@Component({
    selector: 'app-consolidate-periods-form',
    templateUrl: './consolidate-periods-form.component.html',
    styleUrls: ['./consolidate-periods-form.component.scss'],
    standalone: false
})
export class ConsolidatePeriodsFormComponent implements OnInit, AfterViewInit {
  @Input() company: Company;
  @Input() columns: DataViewColumn[];
  @Input() rows: DataViewRow[];
  @Input() frameToBeEditedId: number;
  @Output() closeConsolidatedPeriods = new EventEmitter<boolean>();

  consolidateForm: FormGroup;

  isSubmitting = false;
  isLoading = false;
  buildAttempted = false;
  readonly MAX_SCENARIO_NAME_LENGTH = 200;

  get statementDate(): string | null {
    return this.consolidateForm?.get('statementDate')?.value ?? null;
  }
  get reportingInterval(): string {
    return this.consolidateForm?.get('reportingInterval')?.value ?? '';
  }
  get selectedBalanceSheetFrameIndex(): number {
    const val = this.consolidateForm?.get('balanceSheetFrameIndex')?.value;
    return val === undefined || val === null || val === '' ? -1 : Number(val);
  }
  get selectedBeginningCashFrameIndex(): number {
    const val = this.consolidateForm?.get('beginningCashFrameIndex')?.value;
    return val === undefined || val === null || val === '' ? -1 : Number(val);
  }
  get selectedEndingCashFrameIndex(): number {
    const val = this.consolidateForm?.get('endingCashFrameIndex')?.value;
    return val === undefined || val === null || val === '' ? -1 : Number(val);
  }
  get isFormComplete(): boolean {
    return this.consolidateForm?.valid ?? false;
  }
  get currency(): string {
    return this.consolidateForm?.get('currency')?.value ?? this.company?.settings?.currency;
  }
  set currency(value: string) {
    this.consolidateForm?.patchValue({ currency: value });
  }

  get scenarioType(): string {
    return this.consolidateForm?.get('scenarioType')?.value ?? SCENARIO_TYPE_HISTORICAL;
  }

  get scenarioName(): string {
    return this.consolidateForm?.get('scenarioName')?.value ?? '';
  }

  get isScenarioNameDisabled(): boolean {
    return this.scenarioType === SCENARIO_TYPE_HISTORICAL;
  }

  get availableScenarioTypeOptions(): Select2OptionData[] {
    return availableScenarioTypes(new Set(this._scenariosOfSelectedPeriods()));
  }

  private _scenariosOfSelectedPeriods(): string[] {
    const indices: number[] = this.consolidateForm?.get('selectedPeriodIndices')?.value ?? [];
    return indices.map(idx => this.columns[idx]?.scenario).filter(Boolean);
  }

  updateScenarioType(value: string): void {
    this._hasUserExplicitlySetScenarioType = true;
    this.consolidateForm.patchValue({ scenarioType: value });
    this._syncScenarioNameDisabledState();
  }

  private _updateScenarioDefaults(): void {
    const inputScenarios = new Set(this._scenariosOfSelectedPeriods());
    const resolved = resolveScenarioType(this.scenarioType, this._hasUserExplicitlySetScenarioType, inputScenarios);
    this.consolidateForm.patchValue({ scenarioType: resolved });
    this._syncScenarioNameDisabledState();
  }

  private _syncScenarioNameDisabledState(): void {
    const control = this.consolidateForm?.get('scenarioName');
    if (!control) { return; }
    if (this.isScenarioNameDisabled && control.enabled) {
      control.disable();
    } else if (!this.isScenarioNameDisabled && control.disabled) {
      control.enable();
    }
  }

  reportingIntervalOptions: Select2OptionData[] = [
    {
      id: 'MONTHLY',
      text: 'MONTHLY'
    },
    {
      id: 'QUARTERLY',
      text: 'QUARTERLY'
    },
    {
      id: 'SEMI_ANNUALLY',
      text: 'SEMI_ANNUALLY'
    },
    {
      id: 'ANNUALLY',
      text: 'ANNUALLY'
    },
    {
      id: 'FISCAL_YTD',
      text: 'FISCAL_YTD'
    },
    {
      id: 'TTM',
      text: 'TTM'
    }
  ];

  periodOptions: Select2OptionData[] = [];
  multiSelectPeriodOptions: MultiSelectItem[] = [];
  multiSelectButtonText = 'Select';

  statementDateHasBeenChangedByUser = false;
  hasBeginningCashBeenChangedByUser = false;
  hasEndingCashBeenChangedByUser = false;
  hasBalanceSheetBeenChangedByUser = false;
  private _hasUserExplicitlySetScenarioType = false;
  isBeginningCashInTable = false;
  isEndingCashInTable = false;
  supportedCurrencies = SUPPORTED_CURRENCIES;

  constructor(
    private _dataFrameService: DataFrameService,
    private _alertService: AlertService,
    private _formBuilder: FormBuilder,
  ) { }

  private buildForm(): void {
    this.consolidateForm = this._formBuilder.group({
      selectedPeriodIndices: [[], [atLeastOnePeriodSelected]],
      reportingInterval: ['', Validators.required],
      statementDate: [null, Validators.required],
      balanceSheetFrameIndex: [-1, requiredSelection],
      beginningCashFrameIndex: [-1],
      endingCashFrameIndex: [-1],
      currency: [this.company?.settings?.currency ?? 'USD'],
      scenarioType: [SCENARIO_TYPE_HISTORICAL, Validators.required],
      scenarioName: ['', [Validators.required, noWhitespaceOnly, Validators.maxLength(this.MAX_SCENARIO_NAME_LENGTH)]],
    });
    if (this.isBeginningCashInTable) {
      this.consolidateForm.get('beginningCashFrameIndex').setValidators([requiredSelection]);
    }
    if (this.isEndingCashInTable) {
      this.consolidateForm.get('endingCashFrameIndex').setValidators([requiredSelection]);
    }
    this._syncScenarioNameDisabledState();
  }

  ngOnInit(): void {
    this.setCashFlowStatementFlags();
    this.buildForm();
    if (this.frameToBeEditedId) {
      this._prefillEditForm();
    }
    this.columns.forEach((column, colIdx) => {
      const periodLabel = (column.statementDate + ' - ' + column.preparationType + ' - ' + column.reportingInterval + (column.projectionName ? ' - ' + column.projectionName : ''));
      this.periodOptions.push({
        id: String(colIdx),
        text: periodLabel,
      });
      this.multiSelectPeriodOptions.push({
        id: String(colIdx),
        title: periodLabel,
        action: () => this.includeOrExcludeColumn(colIdx),
        checked: false,
      });
    });
  }

  ngAfterViewInit(): void {
    this.enableTooltips();
  }

  enableTooltips(): void {
    // Bootstrap tooltip initialization
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  _prefillEditForm(): void {
    if (!this.frameToBeEditedId || !this.consolidateForm) { return; }
    this.isLoading = true;
    this._dataFrameService.loadPrefillDataForCombinedFrame(this.frameToBeEditedId)
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe(
        (res) => {
          res.selectedPeriodOptions.forEach((periodUuid: string) => {
            const periodIndex = this.columns.findIndex(col => col.periodKeyUuid === periodUuid);
            if (periodIndex >= 0 && !this.multiSelectPeriodOptions[periodIndex].checked) {
              this.includeOrExcludeColumn(periodIndex);
            }
          });
          const balanceSheetIdx = this.columns.findIndex(col => col.periodKeyUuid === res.selectedBalanceSheet);
          const beginningCashIdx = this.isBeginningCashInTable && res.selectedBeginningCashFrame
            ? this.columns.findIndex(col => col.periodKeyUuid === res.selectedBeginningCashFrame) : -1;
          const endingCashIdx = this.isEndingCashInTable && res.selectedEndingCashFrame
            ? this.columns.findIndex(col => col.periodKeyUuid === res.selectedEndingCashFrame) : -1;
          this.consolidateForm.patchValue({
            reportingInterval: res.reportingInterval,
            statementDate: res.statementDate,
            currency: res.currency,
            balanceSheetFrameIndex: balanceSheetIdx >= 0 ? balanceSheetIdx : -1,
            beginningCashFrameIndex: beginningCashIdx >= 0 ? beginningCashIdx : -1,
            endingCashFrameIndex: endingCashIdx >= 0 ? endingCashIdx : -1,
            scenarioType: res.scenarioType ?? SCENARIO_TYPE_HISTORICAL,
            scenarioName: res.scenarioName ?? '',
          });
          this._hasUserExplicitlySetScenarioType = true;
          this._syncScenarioNameDisabledState();
        },
        (error) => {
          console.error('failed to load prefill values: ', error);
        }
      );
  }

  get hasSelectedPeriods(): boolean {
    const indices = this.consolidateForm?.get('selectedPeriodIndices')?.value;
    return Array.isArray(indices) && indices.length > 0;
  }

  includeOrExcludeColumn(colIdx: number): void {
    const option = this.multiSelectPeriodOptions[colIdx];
    if (!option) { return; }
    option.checked = !option.checked;
    const selectedIndices = this.multiSelectPeriodOptions
      .map((opt, idx) => (opt.checked ? idx : -1))
      .filter(idx => idx >= 0);
    this.consolidateForm.patchValue({ selectedPeriodIndices: selectedIndices });
    this.updateAutofilledNewPeriodData();
    this._updateScenarioDefaults();
    this.updateMultiSelectButtonText();
  }

  updateMultiSelectButtonText(): void {
    const count = this.multiSelectPeriodOptions.filter(opt => opt.checked).length;
    if (count < 1) { this.multiSelectButtonText = 'Select Periods'; }
    else if (count === 1) { this.multiSelectButtonText = '1 period selected'; }
    else { this.multiSelectButtonText = `${count} periods selected`; }
  }

  updateReportingInterval(reportingInterval: string): void {
    this.consolidateForm.patchValue({ reportingInterval });
  }

  updateStatementDate(event: { dateObject: { formatted: string } }) {
    this.consolidateForm.patchValue({ statementDate: event.dateObject.formatted });
    this.statementDateHasBeenChangedByUser = true;
  }

  updateSelectedBalanceSheetFrameIndex(value: string) {
    this.consolidateForm.patchValue({ balanceSheetFrameIndex: Number(value) });
    this.hasBalanceSheetBeenChangedByUser = true;
  }

  updateSelectedBeginningCashFrameIndex(value: string) {
    this.consolidateForm.patchValue({ beginningCashFrameIndex: Number(value) });
    this.hasBeginningCashBeenChangedByUser = true;
  }

  updateSelectedEndingCashFrameIndex(value: string) {
    this.consolidateForm.patchValue({ endingCashFrameIndex: Number(value) });
    this.hasEndingCashBeenChangedByUser = true;
  }

  /** Recomputes form validity. Used by tests. */
  setIsFormComplete(): void {
    this.consolidateForm?.updateValueAndValidity();
  }

  /** True when the control should show error state: a build was attempted and the control is invalid. */
  isControlInvalid(controlName: string): boolean {
    const c = this.consolidateForm?.get(controlName);
    return c ? this.buildAttempted && c.invalid : false;
  }

  /**
   * Returns the scenario name error message when it should be shown, otherwise null.
   * Visibility matches isControlInvalid: a build must have been attempted and the
   * control must be invalid. Errors are not shown on blur/touch so that all field
   * errors surface together only on the Build Statement click.
   */
  getScenarioNameError(): string | null {
    const control = this.consolidateForm?.get('scenarioName');
    if (!control?.invalid) { return null; }
    if (!this.buildAttempted) { return null; }
    const errors = control.errors ?? {};
    if (errors['maxlength']) { return 'Scenario name cannot exceed 200 characters.'; }
    if (errors['required'] || errors['nospace']) { return 'Please enter a Scenario Name.'; }
    return null;
  }

  triggerCloseConsolidatePeriods(newStatementCreated=false) {
    this.closeConsolidatedPeriods.emit(newStatementCreated);
  }

  generateConsolidatedStatement(): void {
    if (!this.consolidateForm || this.consolidateForm.invalid || !this.company || !this.columns?.length) {
      if (this.consolidateForm) {
        this.consolidateForm.markAllAsTouched();
      }
      this.buildAttempted = true;
      return;
    }
    this.buildAttempted = false;
    this.isSubmitting = true;
    const balanceSheetColumn = this.columns[this.selectedBalanceSheetFrameIndex];
    if (!balanceSheetColumn) {
      this.isSubmitting = false;
      this._alertService.error('Please select a balance sheet period.');
      return;
    }
    const sharedRequestArgs: [number, string, string, Array<unknown>, unknown, unknown, unknown, string, unknown, 'CONSOLIDATED' | 'CALCULATED', string, string | undefined] = [
      this.company.id,
      moment(this.statementDate, DATE_FORMAT).format(SERVER_DATE_FORMAT),
      this.reportingInterval,
      this.prepareIncomeStatmentFrames(),
      this.colToFrameKey(balanceSheetColumn),
      this.selectedBeginningCashFrameIndex === -1 ? undefined : this.colToFrameKey(this.columns[this.selectedBeginningCashFrameIndex]),
      this.selectedEndingCashFrameIndex === -1 ? undefined : this.colToFrameKey(this.columns[this.selectedEndingCashFrameIndex]),
      this.currency,
      this._buildFormSnapshot(),
      'CONSOLIDATED',
      this.scenarioType,
      this.isScenarioNameDisabled ? undefined : this.scenarioName?.trim() || undefined,
    ];
    const submitRequest$ = this.frameToBeEditedId
      ? this._dataFrameService.updateCombinedFrame(this.frameToBeEditedId, ...sharedRequestArgs)
      : this._dataFrameService.createCombinedFrame(...sharedRequestArgs);

    submitRequest$.subscribe(
      () => {
        this._alertService.success('Statement Built! If the new statement isn\'t shown, please reload the page.');
        this.triggerCloseConsolidatePeriods(true);
        this.isSubmitting = false;
      },
      (err) => {
        this._alertService.error(err.message);
        this.isSubmitting = false;
      }
    );
  }

  _buildFormSnapshot(): Record<string, unknown> {
    const selectedIndices = this.consolidateForm.get('selectedPeriodIndices').value || [];
    const columns = this.columns;
    const baseSnapshot: Record<string, unknown> = {
      selectedPeriodOptions: selectedIndices
        .map((idx: number) => columns[idx]?.periodKeyUuid)
        .filter(Boolean),
      reportingInterval: this.reportingInterval,
      statementDate: this.statementDate,
      selectedBalanceSheet: columns[this.selectedBalanceSheetFrameIndex]?.periodKeyUuid,
      currency: this.currency,
      scenarioType: this.scenarioType,
      scenarioName: this.isScenarioNameDisabled ? '' : this.scenarioName,
    };
    if (this.isBeginningCashInTable) {
      baseSnapshot.selectedBeginningCashFrame = columns[this.selectedBeginningCashFrameIndex]?.periodKeyUuid;
    }
    if (this.isEndingCashInTable) {
      baseSnapshot.selectedEndingCashFrame = columns[this.selectedEndingCashFrameIndex]?.periodKeyUuid;
    }
    return baseSnapshot;
  }

  prepareIncomeStatmentFrames() {
    const selectedIndices = this.consolidateForm.get('selectedPeriodIndices').value || [];
    const columns = this.columns;
    return selectedIndices
      .filter((idx: number) => columns[idx])
      .map((idx: number) => this.getBuilderFrameForColumn(columns[idx]));
  }

  getBuilderFrameForColumn(column: DataViewColumn) {
    return {
      frame_key: this.colToFrameKey(column),
      operator: STATEMENT_BUILDER_OPERATORS.add
    }
  }

  colToFrameKey(column: DataViewColumn) {
    return {
      scenario: column.scenario,
      statement_date: moment(column.statementDate, DATE_FORMAT).format(SERVER_DATE_FORMAT),
      reporting_interval: column.reportingInterval,
      preparation_type: column.preparationType,
      projection_name: column.projectionName,
    }
  }

  selectOrDeselectAll(): void {
    if (this.multiSelectPeriodOptions.some(option => option.checked === true)) {
      this.multiSelectPeriodOptions.forEach(option => option.checked = false);
      this.consolidateForm.patchValue({ selectedPeriodIndices: [] });
    } else {
      this.multiSelectPeriodOptions.forEach(option => option.checked = true);
      const allIndices = this.multiSelectPeriodOptions.map((_, idx) => idx);
      this.consolidateForm.patchValue({ selectedPeriodIndices: allIndices });
    }
    this.updateAutofilledNewPeriodData();
    this._updateScenarioDefaults();
    this.updateMultiSelectButtonText();
  }

  updateAutofilledNewPeriodData(): void {
    const patch: Partial<{
      balanceSheetFrameIndex: number;
      beginningCashFrameIndex: number;
      endingCashFrameIndex: number;
      statementDate: string;
    }> = {};
    if (!this.hasBalanceSheetBeenChangedByUser) {
      const idx = this.retreiveEarliestOrLatestIncludedColumnIdx(false);
      if (idx !== null) { patch.balanceSheetFrameIndex = idx; }
    }
    if (this.isBeginningCashInTable && !this.hasBeginningCashBeenChangedByUser) {
      const idx = this.retreiveEarliestOrLatestIncludedColumnIdx(true);
      if (idx !== null) { patch.beginningCashFrameIndex = idx; }
    }
    if (this.isEndingCashInTable && !this.hasEndingCashBeenChangedByUser) {
      const idx = this.retreiveEarliestOrLatestIncludedColumnIdx(false);
      if (idx !== null) { patch.endingCashFrameIndex = idx; }
    }
    if (!this.statementDateHasBeenChangedByUser) {
      const date = this.retreiveEarliestOrLatestIncludedColumnStatementDate(false);
      if (date != null) { patch.statementDate = date; }
    }
    if (Object.keys(patch).length && this.consolidateForm) {
      this.consolidateForm.patchValue(patch);
    }
  }

  setCashFlowStatementFlags(): void {
    this.rows.forEach((row) => {
      // Source-item rows (shown via the Source Items toggle) aren't tied to a template lineItem.
      if (!row.lineItem) {
        return;
      }
      if (row.lineItem.id === TAXONOMY_ITEM_IDS.TOTAL_BEGINNING_CASH) {
        this.isBeginningCashInTable = true;
      } else if (row.lineItem.id === TAXONOMY_ITEM_IDS.TOTAL_ENDING_CASH) {
        this.isEndingCashInTable = true;
      }
    });
  }

  retreiveEarliestOrLatestIncludedColumnIdx(getEarliest = true): number | null {
    const columns = this.columns;
    let column: DataViewColumn | null = null;
    let colIdx: number | null = null;
    this.multiSelectPeriodOptions.forEach((option, idx) => {
      if (option.checked) {
        const includedColumn = columns[idx];
        if (!includedColumn?.statementDate) { return; }
        if (column === null ||
          (getEarliest && new Date(includedColumn.statementDate) < new Date(column.statementDate)) ||
          (!getEarliest && new Date(includedColumn.statementDate) > new Date(column.statementDate))) {
          column = includedColumn;
          colIdx = idx;
        }
      }
    });
    return colIdx;
  }

  retreiveEarliestOrLatestIncludedColumnStatementDate(getEarliest = true): string | null {
    const columns = this.columns;
    let column: DataViewColumn | null = null;
    this.multiSelectPeriodOptions.forEach((option, idx) => {
      if (option.checked) {
        const includedColumn = columns[idx];
        if (!includedColumn?.statementDate) { return; }
        if (column === null ||
          (getEarliest && new Date(includedColumn.statementDate) < new Date(column.statementDate)) ||
          (!getEarliest && new Date(includedColumn.statementDate) > new Date(column.statementDate))) {
          column = includedColumn;
        }
      }
    });
    return column?.statementDate ?? null;
  }

  changeCurrency(currency: string) {
    this.consolidateForm.patchValue({ currency });
  }

}
